import { Base } from '@studiometa/js-toolkit';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { nextTick } from '@studiometa/js-toolkit/utils';
import withMapbox from './withMapbox.js';
import { requestGeolocation, reverseGeocodeAddress } from './StoreGeolocation.js';
import { applyStoreFilters, emptyStoreFilters } from './StoreFilterLogic.js';
import {
  computeFraming,
  computeFramingPadding,
  FRAMING_MAX_ZOOM,
  isPrecisePlaceType,
} from './StoreFraming.js';
// F3 (revue finale du 2026-09-24) : `title`/`address`/`img`/`url` viennent
// des données magasin (titre, adresse, image, permalien) et étaient jusqu'ici
// injectées brutes dans `Popup.setHTML()` (contenu ET attributs
// `alt`/`src`/`href`) — un titre contenant des caractères HTML (import,
// contenu non maîtrisé) pouvait donc exécuter du script (XSS stocké).
import escapeHtml from './escapeHtml.js';

// Infobulle d'un magasin isolé (chantier store-locator-pin-concept, décision de
// Robin du 2026-10-06 : « décale la bulle vers le haut »). Les trois marqueurs
// (Concept Store, Corner Store, Revendeur) sont des gouttes 30×36 ancrées par
// leur pointe : elles occupent 15 px de chaque côté et 36 px AU-DESSUS de la
// coordonnée du magasin. Sans décalage, l'infobulle ouverte au-dessus de la
// coordonnée recouvrait donc entièrement le point cliqué.
const MARKER_WIDTH_HALF = 15;
const MARKER_HEIGHT = 36;
// Jeu entre le point et la pointe de la bulle : 6 px, l'écart de l'ancien
// réglage de bureau (bulle 24 px au-dessus d'un marqueur centré dont le haut
// était à 18 px).
const POPUP_GAP = 6;

/**
 * Décalage de l'infobulle selon l'ancrage choisi par Mapbox (position de la
 * bulle par rapport à la coordonnée) :
 * - bulle AU-DESSUS (`bottom*`) : remontée de la hauteur du marqueur + jeu ;
 * - bulle EN DESSOUS (`top*`) : aucun décalage, le marqueur est au-dessus de
 *   la coordonnée ;
 * - bulle sur le CÔTÉ (`left` : bulle à droite, `right` : bulle à gauche) :
 *   écartée de la demi-largeur + jeu, remontée de la moitié de la hauteur
 *   (le marqueur est centré à 18 px au-dessus de la coordonnée).
 * @type {Record<string, [number, number]>}
 */
const STORE_POPUP_OFFSET = {
  top: [0, 0],
  'top-left': [0, 0],
  'top-right': [0, 0],
  bottom: [0, -(MARKER_HEIGHT + POPUP_GAP)],
  'bottom-left': [0, -(MARKER_HEIGHT + POPUP_GAP)],
  'bottom-right': [0, -(MARKER_HEIGHT + POPUP_GAP)],
  left: [MARKER_WIDTH_HALF + POPUP_GAP, -MARKER_HEIGHT / 2],
  right: [-(MARKER_WIDTH_HALF + POPUP_GAP), -MARKER_HEIGHT / 2],
};

// Classe posée sur le conteneur de la carte tant qu'une infobulle de magasin est
// ouverte (voir `watchStorePopup`) ; lue uniquement par `archive-stores.css`.
const STORE_POPUP_OPEN_CLASS = 'has-store-popup';

/**
 * MapboxMarker
 */
export default class MapboxCluster extends withMapbox(Base) {
  static config = {
    name: 'MapboxCluster',
    options: {
      source: Array,
      id: String,
      is_basic: Boolean,
    },
  };

  geocoder;

  filteredItems;

  // Filtres visibles de la barre de puces (chantier store-locator-revendeurs,
  // point 6, critère 3), diffusés par `StoreFilterBar` via l'évènement
  // `store-locator-filters` — remplace l'ancien `typeFilter` alimenté par
  // `FilterPanel` (retiré de cette page, voir StoreFilterBar.js). Appliqués
  // ici pour que la carte reste cohérente avec la liste (`StoreResultsList`
  // applique le même filtre indépendamment, invariant R9 : la liste et les
  // filtres fonctionnent sans attendre la carte).
  activeFilters = emptyStoreFilters();

  // Point de référence « précis » courant (chantier store-locator-zoom-geoloc,
  // critères 5 et 7b) : position acceptée par le visiteur, ou résultat de
  // recherche de type adresse / lieu précis / quartier. Tant qu'il existe,
  // la carte se recadre sur ce point et sur les magasins les plus proches à
  // chaque changement de filtre. Une recherche de ville, de code postal ou
  // de région, ou l'effacement de la recherche, le remet à `null`. Jamais
  // mémorisé : état en mémoire de la page seulement (`ndStoreLocatorRef`,
  // géré par `StoreResultsList`, reste le seul stockage).
  preciseReference = null;

  /**
   * @returns {Array}
   */
  get geoJson() {
    return {
      type: 'FeatureCollection',
      features: this.filteredItems.map(this.itemToGeoJsonFeature),
    };
  }

  /**
   * mounted
   */
  mounted() {
    this.filteredItems = this.$options.source;

    if (! this.$options.is_basic) {
      // Filtres déjà cochés avant le montage de la carte (chantier
      // store-locator-zoom-geoloc, revue ZF1) : ce composant n'est monté qu'une
      // fois la carte chargée, alors que `StoreFilterBar` ne diffuse son état
      // qu'à son propre montage et à chaque clic — un filtre coché pendant le
      // chargement serait ignoré ici (cadrage et marqueurs sans filtre, puis
      // « Aucun revendeur » possible dans la liste). Si au moins une puce est
      // active, la barre est priée de rediffuser son état avant tout cadrage ;
      // sans puce active, rien n'est diffusé (état initial inchangé).
      if (document.querySelector('.chip[aria-pressed="true"]')) {
        window.dispatchEvent(new CustomEvent('store-locator-filters-request'));
      }
      this.addGeocoder();
      this.bindGeolocateButton();
      // Géolocalisation à l'arrivée (chantier store-locator-revendeurs,
      // point 5, critère 1) : uniquement ici, dans la branche `!is_basic`.
      // Les pages « ville » (template-lp.php) et une éventuelle page
      // d'accueil reprenant la carte en mode `is_basic` ne passent jamais
      // par cette branche : aucune demande de position n'y est jamais faite
      // (invariant R8, scénario 54).
      this.requestPosition();
    }
    this.addImages();
    this.addSourceData();
    this.addClusterLayer();
    this.addClusterCountLayer();
    this.addUnclusterPointLayer();
    this.onClusterClick();
    this.onUnclusterClick();
    this.watchStorePopup();

    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('moveend', () => {
      this.filterFeaturesInView();

      nextTick(() => {
        this.map.getSource('stores').setData(this.geoJson);
        this.broadcastItems();
      });
    });

    if (document.body.classList.contains('page-template-template-lp')) {
      if (this.filteredItems.length > 0) {
        const bounds = new this.mapbox.LngLatBounds();

        this.filteredItems.forEach(({ lng, lat }) => {
          bounds.extend([lng, lat]);
        });

        this.map.once('idle', () => {
          this.map.fitBounds(bounds, { padding: 50, maxZoom: 12, duration: 1000 });
        
          setTimeout(() => {
            const currentZoom = this.map.getZoom();
            this.map.easeTo({
              zoom: currentZoom - 0.1, // Ajuste cette valeur si nécessaire
              duration: 500, // Durée de l'animation en ms
              easing: t => t * (2 - t) // Fonction d'interpolation douce
            });
          }, 1000); // Attendre que `fitBounds` ait fini
        });
      }
    }
  }

  /**
   * Add Source Data
   */
  addSourceData() {
    this.source = this.map.addSource('stores', {
      type: 'geojson',
      data: this.geoJson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
    this.broadcastItems();
  }

  /**
   * Diffuse les magasins actuellement visibles (zone de la carte ∩ filtre
   * type) au composant `StoreResultsList` (chantier store-locator-revendeurs,
   * point 4), avec le centre courant de la carte pour le tri par distance
   * quand aucune recherche n'est en cours (critère 5, scénario 12a).
   * `StoreResultsList` est autonome (invariant R9) : sur les pages en mode
   * `is_basic` (ex. `template-lp.php`), il n'existe pas de `#list` à mettre à
   * jour, cet évènement n'a donc aucun effet et n'a pas besoin d'être émis.
   */
  broadcastItems() {
    if (this.$options.is_basic) {
      return;
    }
    const center = this.map.getCenter();
    window.dispatchEvent(new CustomEvent('store-locator-items', {
      detail: {
        items: this.filteredItems,
        center: [center.lng, center.lat],
      },
    }));
  }

  /**
   * add geocoder
   */
  addGeocoder() {
    this.geocoder = new MapboxGeocoder({
      accessToken: this.mapbox.accessToken,
      mapboxgl: this.mapbox,
      marker: false,
      language: 'fr',
      countries: 'fr',
      // Maquette (point 6, section « Rendu visuel ») : remplace le
      // placeholder par défaut du plugin (« Chercher »).
      placeholder: 'Entrez une adresse, une ville ou un code postal',
    });

    document.querySelector('#geocoder').append(this.geocoder.onAdd(this.map));

    // Point de référence pour la distance (invariant R6, critère 5) : diffusé
    // à `StoreResultsList`, qui le mémorise pour l'onglet (sessionStorage
    // `ndStoreLocatorRef`, voir StoreReferencePoint.js) sans jamais toucher
    // aux clés du magasin de retrait ni à un cookie.
    //
    // Cadrage (chantier store-locator-zoom-geoloc, critère 5) : le geocoder
    // déclenche sa propre animation (zoom 16 ou emprise de la zone) de façon
    // synchrone AVANT d'émettre `result`. Pour une adresse, une rue, un lieu
    // précis ou un quartier (liste blanche `PRECISE_PLACE_TYPES`), on la
    // remplace ici par le cadrage sur le point et les magasins les plus
    // proches ; pour tout autre type (ville, code postal, département,
    // région, pays…), l'animation du geocoder reste inchangée (la carte se
    // cadre sur la zone trouvée, sans élargissement).
    this.geocoder.on('result', (event) => {
      const [lng, lat] = event.result.center;
      window.dispatchEvent(new CustomEvent('store-locator-reference', { detail: { lng, lat } }));
      if (isPrecisePlaceType(event.result.place_type)) {
        this.frameOnPoint({ lng, lat });
      } else {
        this.preciseReference = null;
      }
    });
    this.geocoder.on('clear', () => {
      this.preciseReference = null;
      window.dispatchEvent(new CustomEvent('store-locator-reference', { detail: null }));
    });
  }

  /**
   * Fixe le point de référence précis puis cadre la carte dessus (position
   * acceptée, recherche précise). Voir `frameMap()`.
   * @param {{lng: number, lat: number}} point
   */
  frameOnPoint(point) {
    this.preciseReference = point;
    this.frameMap();
  }

  /**
   * Cadre la carte sur le point de référence précis et les magasins les plus
   * proches : les 5 plus proches sans filtre, le plus proche correspondant
   * aux filtres si un filtre est actif (repli sur les 5 plus proches si
   * aucun ne correspond) — calcul pur dans `StoreFraming.js`, qui réutilise
   * la distance de la liste. Zoom maximal 14 (niveau quartier), marges qui
   * évitent la légende et les boutons de la carte. Aucun effet sans point de
   * référence précis : les filtres ne déplacent alors pas la carte. Jamais
   * appelé sur un zoom, un déplacement ou le rafraîchissement minute de
   * « Ouvert maintenant » : le visiteur garde la main sur la carte.
   */
  frameMap() {
    if (!this.preciseReference || !this.map) {
      return;
    }
    const framing = computeFraming({
      items: this.$options.source,
      origin: this.preciseReference,
      filters: this.activeFilters,
      now: new Date(),
    });
    if (!framing) {
      return;
    }
    const { west, south, east, north } = framing.bounds;
    const container = this.map.getContainer();
    const padding = computeFramingPadding({
      width: container.clientWidth,
      height: container.clientHeight,
      legendVisible: this.isLegendPresent(container),
    });
    // Le zoom tient compte des marges, mais elles ne sont PAS laissées sur la
    // carte : `fitBounds({ padding })` les conserve comme marge permanente de
    // la caméra, et `getBounds()` (qui alimente la liste « zone visible »)
    // exclurait alors les bandes de marge — le magasin cadré au bord de la
    // marge sortirait de la liste. La marge est donc convertie en décalage du
    // centre (`offset`) : la zone visible reste toute la carte, comme avant.
    const camera = this.map.cameraForBounds([[west, south], [east, north]], {
      padding,
      maxZoom: FRAMING_MAX_ZOOM,
    });
    if (!camera) {
      return;
    }
    this.map.flyTo({
      center: camera.center,
      zoom: camera.zoom,
      bearing: 0,
      pitch: 0,
      offset: [(padding.left - padding.right) / 2, (padding.top - padding.bottom) / 2],
    });
  }

  /**
   * La légende fait-elle partie de l'affichage, indépendamment de son
   * masquage temporaire par une infobulle ouverte ? (chantier
   * store-locator-pin-concept, recontrôle : un recadrage déclenché bulle
   * ouverte — bouton « recentrer sur ma position », position acceptée
   * tardivement — devait garder la marge haute de la légende, sinon des points
   * se retrouvaient sous elle à son retour.) La classe d'état de l'infobulle
   * est retirée le temps de la mesure puis remise dans le même passage : aucun
   * affichage n'a lieu entre les deux, et l'observateur des infobulles ne suit
   * que les ajouts/retraits d'enfants, pas les classes. Aperçu mobile (légende
   * absente en permanence) : toujours faux.
   * @param  {HTMLElement} container Conteneur de la carte.
   * @returns {boolean}
   */
  isLegendPresent(container) {
    const legend = container.querySelector('.map-legend');
    if (!legend) {
      return false;
    }
    const hiddenByPopup = container.classList.contains(STORE_POPUP_OPEN_CLASS);
    if (hiddenByPopup) {
      container.classList.remove(STORE_POPUP_OPEN_CLASS);
    }
    const present = legend.offsetWidth > 0;
    if (hiddenByPopup) {
      container.classList.add(STORE_POPUP_OPEN_CLASS);
    }
    return present;
  }

  /**
   * Bouton « Me géolocaliser » (chantier store-locator-revendeurs, point 5,
   * critères 1-2, scénarios 3-4) : rendu par archive-stores.php à côté de
   * `#geocoder`, disponible que la demande automatique à l'arrivée ait
   * réussi, échoué ou n'ait jamais pu être faite. Un clic relance
   * `requestPosition()` : le navigateur redemande l'autorisation ou indique
   * qu'elle est bloquée, la page reste utilisable dans les deux cas
   * (scénario 4) — aucun traitement spécifique nécessaire ici, le même
   * repli silencieux s'applique.
   */
  bindGeolocateButton() {
    this.geolocateButton = document.getElementById('store-locator-geolocate');
    this.geolocateButton?.addEventListener('click', () => this.requestPosition());
  }

  /**
   * Demande la position courante (à l'arrivée, ou sur clic du bouton). Un
   * succès cadre la carte sur la position et les magasins les plus proches,
   * et diffuse le point de référence ; un refus, un
   * blocage, une indisponibilité ou un délai dépassé ne fait rien de plus
   * (aucune erreur visible, critère 2) : la page reste sur son état déjà
   * rendu (carte France entière, barre de recherche vide, liste triée au
   * centre de la carte sans distance affichée — scénario 12a).
   */
  requestPosition() {
    requestGeolocation({
      onSuccess: ({ lng, lat }) => this.applyGeolocatedPosition(lng, lat),
      onFallback: () => {},
    });
  }

  /**
   * Cadre la carte sur la position acceptée et les magasins les plus proches
   * (chantier store-locator-zoom-geoloc : remplace l'ancien zoom 16 au niveau
   * de la rue, qui ne montrait qu'un magasin en centre-ville et aucun sur
   * mobile) puis diffuse le point de
   * référence (tri par distance + mémorisation `sessionStorage` dédiée via
   * StoreResultsList/StoreReferencePoint.js, invariant R6) avant même que le
   * géocodage inverse ne réponde : le classement par distance et l'affichage
   * des distances (critère 1) ne dépendent donc pas de la réussite de
   * l'étape suivante. Écrit ensuite l'adresse reconnue dans la barre de
   * recherche si le géocodage inverse en trouve une ; sinon la barre reste
   * simplement vide, sans erreur (même repli que le reste du critère 1-2).
   * @param {number} lng
   * @param {number} lat
   */
  async applyGeolocatedPosition(lng, lat) {
    this.frameOnPoint({ lng, lat });
    window.dispatchEvent(new CustomEvent('store-locator-reference', { detail: { lng, lat } }));

    const placeName = await reverseGeocodeAddress({ lng, lat, accessToken: this.mapbox.accessToken });
    if (placeName && this.geocoder) {
      this.geocoder.setInput(placeName);
    }
  }

  /**
   * Add Images to map context
   *
   * Un marqueur distinct par type (chantier store-locator-revendeurs,
   * point 6, section « Rendu visuel » : rond jaune « n » Concept Store,
   * goutte corner, goutte noire revendeur), en SVG (fichiers du thème,
   * `assets/marker-*.svg`). L'identifiant de chaque image correspond
   * désormais directement au slug de la taxonomie `type` (voir `icon` dans
   * archive-stores.php), avec `revendeur` comme image de repli pour un
   * magasin sans terme.
   *
   * `map.loadImage()` (utilisé pour les anciennes icônes PNG `pin`/
   * `pin-reseller`) refuse explicitement les SVG (« SVGs are not
   * supported »), constaté en local en réutilisant ce point. Un simple
   * `<img>` chargé normalement par le navigateur les rasterise sans
   * problème (comme n'importe quelle image `<img src="*.svg">` sur une
   * page) ; `map.addImage()` accepte un `HTMLImageElement` directement, donc
   * chaque image est ajoutée dans son gestionnaire `onload`, sans passer par
   * `loadImage()`. Une image manquante (erreur réseau) ne bloque jamais le
   * reste de la carte : `onerror` reste silencieux, seul ce type de marqueur
   * resterait alors invisible.
   */
  addImages() {
    const base = `${window.location.origin}/wp-content/themes/Divi-child/assets`;
    const markers = [
      ['concept-store', `${base}/marker-concept-store.svg`],
      ['corner-store', `${base}/marker-corner-store.svg`],
      ['revendeur', `${base}/marker-revendeur.svg`],
    ];

    markers.forEach(([id, url]) => {
      const image = new Image();
      image.onload = () => {
        if (!this.map.hasImage(id)) {
          this.map.addImage(id, image);
        }
      };
      image.onerror = () => {};
      image.src = url;
    });
  }

  /**
   * addClusterLayer
   * @see https://docs.mapbox.com/style-spec/reference/expressions/#step
   * for step expressions
   */
  addClusterLayer() {
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'stores',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#000',
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
      },
    });
  }

  /**
   * addClusterCountLayer
   */
  addClusterCountLayer() {
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'stores',
      filter: ['has', 'point_count'],
      paint: {
        'text-color': '#ffffff',
      },
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 16,
      },
    });
  }

  /**
   * addUnclusterPointLayer
   */
  addUnclusterPointLayer() {
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'stores',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['get', 'icon'],
        // Marqueurs SVG dédiés (30×36, point 6) : taille native, contre 0.5
        // pour les anciens PNG 48×48 (avant ce chantier).
        'icon-size': 1,
        // Ancrage (chantier store-locator-pin-concept) : les trois marqueurs
        // (Concept Store, Corner Store, Revendeur) sont des gouttes dont la
        // pointe est en bas-centre du fichier (15, 36) ; ancrés par le bas,
        // c'est cette pointe qui tombe sur la coordonnée du magasin. Avant ce
        // chantier, Mapbox les ancrait par leur centre (réglage par défaut) et
        // la pointe tombait 18 px sous la coordonnée ; Corner Store et
        // Revendeur ont été recalés sur décision de Robin du 2026-10-06.
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
      paint: {},
    });
  }

  /**
   * onClusterClick
   */
  onClusterClick() {
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      this.map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        this.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom,
        });
      });
    });
  }

  /**
   * onUnclusterClick
   */
  onUnclusterClick() {
    this.map.on('click', 'unclustered-point', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const { title, url, img, address, type } = e.features[0].properties;

      // Échappement (F3, revue finale du 2026-09-24) : `title` est du texte
      // brut décodé côté serveur (archive-stores.php/template-lp.php, pour
      // éviter un double encodage), jamais échappé avant ce point — une
      // fiche dont le titre contiendrait des métacaractères HTML (import,
      // contenu non maîtrisé) exécuterait du script une fois injecté tel
      // quel dans l'infobulle (contenu ET attribut `alt`). `url`/`img`
      // (permalien, image) échappés par prudence pour leurs attributs
      // `href`/`src`. `address` n'est PAS réappliqué ici : déjà nettoyé côté
      // serveur par `htmlspecialchars()` avant l'encodage JSON (mêmes
      // sources que `StoreCardMarkup.js` — le réappliquer produirait un
      // double encodage des entités).
      const safeTitle = escapeHtml(title);
      const safeUrl = escapeHtml(url);
      const safeImg = escapeHtml(img);

      // Ensure that if the this.map is zoomed out such that
      // multiple copies of the feature are visible, the
      // popup appears over the copy being pointed to.
      if (['mercator', 'equirectangular'].includes(this.map.getProjection().name)) {
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
      }

      // `offset` par ancrage : l'infobulle laisse le point cliqué visible
      // (STORE_POPUP_OFFSET). Les marges verticales de `.mapboxgl-popup`
      // (archive-stores.css : +45 px en mobile, -24 px dès 981 px) étaient
      // calibrées pour l'ancien marqueur centré et s'appliquaient à tous les
      // ancrages : elles sont neutralisées pour CETTE infobulle, le décalage
      // étant désormais entièrement porté par `offset`.
      const popup = new this.mapbox.Popup({ offset: STORE_POPUP_OFFSET })
        .setLngLat(coordinates)
        .setHTML(
          `
          <a class="map-popup" href="${safeUrl}">
            ${img ? `<img src="${safeImg}" alt="${safeTitle}" width="282" height="158" />` : ``}
            <p class="map-popup__title">
              ${
                // Petit picto « n » dans l'infobulle pour un Concept Store
                // (chantier store-locator-revendeurs, point 6 : corrige une
                // inversion — avant ce point, `icon` valait « revendeur »
                // *pour* les Concept Store, par un héritage du point 1 ;
                // `icon` correspond désormais directement au type, ce picto
                // se base donc sur `type`, comme MapboxMarker.js).
                type === 'concept-store'
                  ? `<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect y="0.567871" width="24" height="24" rx="12" fill="#F6BB41"/>
                      <path d="M7.63574 6.95241H8.66728V8.05109C9.46687 7.14162 10.6632 6.56787 11.9999 6.56787C14.3743 6.56787 16.3092 8.38679 16.3641 10.6574V18.5679H15.3326V10.6574C15.2777 8.93613 13.8066 7.55668 11.9999 7.55668C10.1932 7.55668 8.72221 8.93613 8.66728 10.6574V18.5679H7.63574V6.95241Z" fill="#000"/>
                     </svg>`
                  : ``
              }
              ${safeTitle}
            </p>
            <p class="map-popup__address">${address}</p>
          </a>
        `,
        )
        .addTo(this.map);
      popup.getElement().style.marginTop = '0';
    });
  }

  /**
   * watchStorePopup
   *
   * Légende masquée tant qu'une infobulle de magasin est ouverte (chantier
   * store-locator-pin-concept, décision de Robin du 2026-10-06 : « masquons
   * quand la bulle est ouverte, puis la réafficher si la bulle est fermée »,
   * plein écran mobile seulement). Le script ne pose qu'une classe d'état
   * (`STORE_POPUP_OPEN_CLASS`) sur le conteneur de la carte ; c'est la feuille
   * de style (`archive-stores.css`, largeur mobile et plein écran) qui masque la
   * légende. Les autres affichages (aperçu mobile : légende déjà absente ;
   * bureau : légende toujours visible) ne sont donc jamais modifiés.
   *
   * L'état est recalculé à partir de la présence réelle d'une infobulle dans
   * le conteneur de la carte, jamais d'un compteur d'évènements : une
   * infobulle est ajoutée au conteneur à son ouverture et retirée à sa
   * fermeture (bouton, clic sur la carte, remplacement par la bulle d'un autre
   * point), quel que soit le chemin de fermeture. Les modifications du
   * conteneur sont observées et traitées avant le prochain affichage : lorsqu'une
   * bulle en remplace une autre dans le même clic, la légende ne réapparaît
   * pas entre les deux.
   * Si la feuille de style en cache est plus ancienne que ce script (ou l'inverse),
   * la classe n'a aucun effet : la légende reste visible, comme avant ce chantier.
   */
  watchStorePopup() {
    const container = this.map.getContainer();
    const refresh = () => {
      const open = Boolean(container.querySelector(':scope > .mapboxgl-popup'));
      container.classList.toggle(STORE_POPUP_OPEN_CLASS, open);
    };
    this.popupObserver = new MutationObserver(refresh);
    this.popupObserver.observe(container, { childList: true });
    refresh();
  }

  /**
   * Transform an item into a valid GeoJSON feature.
   * @param  {object} item
   * @param  {number} item.lat
   * @param  {number} item.lng
   * @returns {Feature}
   */
  itemToGeoJsonFeature({ lat, lng, ...properties }) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      properties,
    };
  }

  /**
   * filterFeaturesInView
   *
   * Restreint aux magasins de la zone visible de la carte, puis aux filtres
   * visibles actifs (chantier store-locator-revendeurs, point 6, critère 3 :
   * types en OU, « Ouvert maintenant » et chaque service en ET — voir
   * `StoreFilterLogic.js`, partagé avec `StoreResultsList` pour que la carte
   * reste cohérente avec la liste). Le tri n'a plus lieu ici (point 4,
   * critère 5) : la « priorité d'affichage » et le tri alphabétique qui
   * l'accompagnait ne sont plus utilisés pour cette liste — seuls le type
   * (Concept Store, Corner Store, puis le reste, chantier
   * store-locator-ordre-types) puis la distance comptent, calculés et triés
   * par `StoreResultsList` (voir `broadcastItems()`). L'ordre de `filteredItems` ici n'a pas d'incidence
   * visible : il alimente le tracé des pastilles sur la carte (non ordonné)
   * et les magasins diffusés à la liste, qui les re-trie elle-même.
   *
   * Un magasin hors filtre n'est ici pas dessiné du tout sur la carte
   * (simplification assumée par rapport à la maquette de Robin, qui l'affiche
   * en plus faible opacité — section « Rendu visuel » de la spec : seule
   * l'apparence de la maquette est reprise, pas nécessairement chaque détail)
   * : ce choix reste cohérent avec la liste, qui masque elle aussi les
   * magasins hors filtre plutôt que de les afficher grisés.
   */
  filterFeaturesInView() {
    const mapBounds = this.map.getBounds();

    this.filteredItems = this.$options.source.filter(({ lng, lat }) => mapBounds.contains([lng, lat]));
    this.filteredItems = applyStoreFilters(this.filteredItems, this.activeFilters);
  }

  /**
   * Reçoit les filtres actifs diffusés par `StoreFilterBar` (barre de puces,
   * point 6) : types cumulés, « Ouvert maintenant », services — remplace les
   * anciens évènements `filter`/`filterreset` de `FilterPanel` (retiré de
   * cette page).
   * @param {CustomEvent} event
   */
  async onWindowStoreLocatorFilters({ detail }) {
    this.activeFilters = detail || emptyStoreFilters();
    this.filterFeaturesInView();

    nextTick(() => {
      this.map.getSource('stores').setData(this.geoJson);
      this.broadcastItems();
    });

    // Recadrage aux filtres (chantier store-locator-zoom-geoloc, critère
    // 7b) : filtre coché ou décoché alors qu'un point précis est actif
    // (position acceptée ou recherche précise) → la carte se recadre sur ce
    // point et le magasin correspondant le plus proche (ou les 5 plus
    // proches sans filtre). Sans point précis, les filtres ne déplacent pas
    // la carte (comportement inchangé). Le `moveend` qui suit actualise la
    // liste sur la nouvelle zone.
    this.frameMap();
  }

  /**
   * Rafraîchissement minute du statut d'ouverture (F9, revue finale du
   * chantier store-locator-revendeurs, 2026-09-25), diffusé par
   * `StoreOpeningStatus.js` : sans ce ré-affichage, un magasin qui passe
   * « Fermé » à la minute pile restait visible sur la CARTE (marqueur) même
   * quand `StoreResultsList` le retirait déjà de la liste (F5) — les deux
   * n'étaient plus cohérents entre eux. Même filtrage que
   * `onWindowStoreLocatorFilters` ci-dessus, sans changer `activeFilters` ;
   * aucun effet si « Ouvert maintenant » n'est pas actif, pour ne jamais
   * redessiner la carte inutilement chaque minute.
   */
  onWindowStoreLocatorMinuteTick() {
    if (!this.activeFilters.openNow) {
      return;
    }
    this.filterFeaturesInView();

    nextTick(() => {
      this.map.getSource('stores').setData(this.geoJson);
      this.broadcastItems();
    });
  }

  /**
   * destroyed
   */
  destroyed() {
    this.popupObserver?.disconnect();
    this.map?.getContainer?.().classList.remove(STORE_POPUP_OPEN_CLASS);
    this.marker?.remove();
  }
}
